import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import moment from "moment-timezone";
import { GetActivityListing } from "../../../redux/features/ActivityService";
import { CSVLink } from "react-csv";
import Loader from "../../Common/Loader";

const ACTIVITY_TIMEZONE = "America/Los_Angeles";

const formatActivityTimestamp = (value) => {
  if (!value) return "-";
  return moment
    .utc(value)
    .tz(ACTIVITY_TIMEZONE)
    .format("MM/DD/yyyy @ HH:mm z");
};

const columns = [
  {
    id: "fullName",
    name: "Full Name",
    selector: (row) => (row.fullName ? row.fullName : "-"),
    sortable: true,
    reorder: true,
  },
  {
    id: "email",
    name: "Email",
    selector: (row) => (row.email ? row.email : "-"),
    sortable: true,
    reorder: true,
  },
  {
    id: "ipAddress",
    name: "IP Address",
    selector: (row) => (row.ipAddress ? row.ipAddress : "-"),
    sortable: true,
    reorder: true,
  },
  {
    id: "eventType",
    name: "Event Type",
    selector: (row) => {
      if (row.activityName) {
        return row.activityName;
      }
      if (row.fileName && row.fileType) {
        return "File Upload";
      }
      return "-";
    },
    sortable: true,
    reorder: true,
  },
  {
    id: "target",
    name: "Target",
    selector: (row) => {
      if (row.target) {
        return row.target;
      }
      if (row.trustName) {
        return row.trustName;
      }
      return "-";
    },
    sortable: true,
    reorder: true,
  },
  {
    id: "details",
    name: "Details",
    selector: (row) => {
      if (row.details) {
        return row.details;
      }
      if (row.fileName && row.fileType) {
        return `Uploaded file ${row.fileName} (Type: ${row.fileType})`;
      }
      return "-";
    },
    sortable: true,
    reorder: true,
  },
  {
    id: "updatedAt",
    name: "Time Stamp",
    selector: (row) => formatActivityTimestamp(row.updatedAt),
    sortable: true,
    sortFunction: (rowA, rowB) => {
      const timeA = new Date(rowA.updatedAt || 0).getTime();
      const timeB = new Date(rowB.updatedAt || 0).getTime();
      return timeA - timeB;
    },
    reorder: true,
  },
];

function csvRowEventType(data) {
  if (data?.activityName) return data.activityName;
  if (data?.fileName && data?.fileType) return "File Upload";
  return "-";
}

function csvRowTarget(data) {
  if (data?.target) return data.target;
  return data?.trustName || "-";
}

function csvRowDetails(data) {
  if (data?.details) return data.details;
  if (data?.fileName && data?.fileType) {
    return `Uploaded file ${data.fileName} (Type: ${data.fileType})`;
  }
  return "-";
}

const ActivityLog = () => {
  const dispatch = useDispatch();
  const { getActivityListing } = useSelector((state) => state.activity);
  const [csvData, setCsvData] = useState([]);
  const [isCsvDataShow, setCsvDataShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(GetActivityListing({ search: "" })).finally(() =>
      setLoading(false)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headers = [
    { label: "Full Name", key: "fullName" },
    { label: "Email", key: "email" },
    { label: "IP Address", key: "ipAddress" },
    { label: "Event Type", key: "eventType" },
    { label: "Target", key: "target" },
    { label: "Details", key: "details" },
    { label: "Time Stamp", key: "updatedAt" },
  ];

  const csvLink = useRef();
  const handleCsvDownload = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        GetActivityListing({
          csv: true,
        })
      );

      const appendedData = _.map(response?.payload?.data, (data) => ({
        fullName: data?.fullName ? data.fullName : "-",
        email: data?.email || "-",
        ipAddress: data?.ipAddress || "-",
        eventType: csvRowEventType(data),
        target: csvRowTarget(data),
        details: csvRowDetails(data),
        updatedAt: formatActivityTimestamp(data.updatedAt),
      }));

      if (Array.isArray(appendedData)) {
        const sortedData = _.sortBy(appendedData, "fullName");
        setCsvData(sortedData);
        setCsvDataShow(true);
      } else {
        console.error("Invalid data format for CSV export");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (csvData?.length > 0 && isCsvDataShow) {
      csvLink.current.link.click();
      setCsvDataShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvData]);

  return (
    <div className="w-100">
      <div className="title_bar d-flex align-items-center justify-content-between">
        <div className="text-style">Activity Log</div>
        <div className="d-flex bttn_continue mb-0">
          {getActivityListing.data && (
            <>
              <button
                className="btn btn_continue my-0"
                onClick={handleCsvDownload}
              >
                Export CSV
              </button>
              <CSVLink
                data={csvData}
                filename="Activity-Log"
                ref={csvLink}
                headers={headers}
              />
            </>
          )}
        </div>
      </div>
      {loading && <Loader />}
      <div className="conten-div mx-1">
        <div className="TableData">
          <DataTable
            columns={columns}
            data={getActivityListing?.data}
            pagination
            highlightOnHover
            defaultSortFieldId="fullName"
          />
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
